// src/components/Dashboard/Patrolling/PatrollingDashboard.js
import { Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { XCircle } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import employeeIcon from '../../../assets/employee.png';

// Import components
import { useScreenLogging } from '../../../hooks/useScreenLogging';
import convoyDetailsCache from '../../../services/convoyDetailsCache';
import dashboardCache from '../../../services/liveRideDashboardCache.js';
import { driverApi } from '../../Driver/driverApi.js';
import { patrollingApi } from '../../Patrolling/PatrollingApi.js';
import { qrApi } from '../../QR/QRApi.js';
import { getSiteDetails } from '../../SiteManagement/siteApi.js';
import MapComponent from '../MapComponent.jsx';
import { dashboardApi } from '../dashboardApi.js';
import { patrollingLocationApi } from './PatrollingLocationApi.js';
// COMMENTED OUT: Using real QR location data instead of mock route generation
// import { generateTestPatrolRoute } from './generateRandomLocations';
import TrackPatrollingComponent from './TrackPatrollingComponent.jsx';

// Utility function to combine Tailwind classes
const combineClasses = (...classes) => classes.filter(Boolean).join(' ');

// Glassmorphism styles
const glassmorphism = 'bg-white/20 backdrop-blur-md rounded-xl border border-white/10 shadow-lg';

const PatrollingDashboard = ({ isAuthenticated }) => {
  // Log screen view
  useScreenLogging('PatrollingDashboard', {
    component: 'PatrollingDashboard',
    isAuthenticated: !!isAuthenticated,
  });
  // State for data
  const [livePatrols, setLivePatrols] = useState([]);
  const [officeLocations, setOfficeLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedPatrolForMap, setSelectedPatrolForMap] = useState(null);
  
  // State for map
  const [mapCenter, setMapCenter] = useState({ lat: 20.5937, lng: 78.9629 }); // Default center for India
  const [mapZoom, setMapZoom] = useState(5);
  
  // Store all office locations (unfiltered)
  const [allOfficeLocations, setAllOfficeLocations] = useState([]);
  
  // State for officer tracking
  const [selectedOfficer, setSelectedOfficer] = useState(null); // {patrolId, userId, officerName}
  
  // State for all patrol routes (when no specific patrol is selected)
  const [allPatrolRoutes, setAllPatrolRoutes] = useState([]);
  
  // Store generated routes persistently to avoid regenerating randomly
  const persistentRoutesRef = useRef(new Map()); // Map<patrol_id, route>

  // State for live patroller locations (selected patrol)
  const [patrolLiveLocations, setPatrolLiveLocations] = useState([]);
  
  // State for patrol employees (dummy employees near QR locations)
  const [patrolEmployees, setPatrolEmployees] = useState([]);
  
  // Store generated employees persistently to avoid regenerating randomly
  const persistentEmployeesRef = useRef(new Map()); // Map<patrol_id, employees>
  
  // State for patrol info table
  const [showPatrolInfoTable, setShowPatrolInfoTable] = useState(false);
  const [patrolTableData, setPatrolTableData] = useState([]);
  const [allPatrolTableData, setAllPatrolTableData] = useState([]); // Store all data for filtering
  const [loadingTableData, setLoadingTableData] = useState(false);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(null); // null = all groups, number = group index
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for mobile viewport detection
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 768);
  
  const mapComponentRef = useRef();
  
  // Cache for officer details to avoid repeated API calls
  const officerDetailsCacheRef = useRef(new Map()); // Map<userId, { name, timestamp }>
  
  // Cache for driver details to avoid repeated API calls
  const driverDetailsCacheRef = useRef(new Map()); // Map<userId, { user_name, timestamp }>
  
  // State for QR lookup (to display QR names instead of IDs)
  const [qrLookup, setQrLookup] = useState({});
  // State for QR locations of the selected patrol (ordered path)
  const [patrolQRLocations, setPatrolQRLocations] = useState([]);

  // Helper function to get unique colors for each patrol route
  const getPatrolColor = useCallback((index) => {
    const colors = [
      '#ef4444', // Red
      '#3b82f6', // Blue
      '#22c55e', // Green
      '#f59e0b', // Orange
      '#8b5cf6', // Purple
      '#ec4899', // Pink
      '#14b8a6', // Teal
      '#f97316', // Dark Orange
      '#06b6d4', // Cyan
      '#84cc16', // Lime
    ];
    return colors[index % colors.length];
  }, []);

  // Helper function to generate dummy employees near QR locations
  // COMMENTED OUT: Using real employee data from API instead of mock data
  // const generateDummyEmployeesNearQR = useCallback((route) => {
  //   const employees = [];
  //   const employeeNames = [
  //     'John Smith', 'Sarah Johnson', 'Michael Brown', 'Emily Davis',
  //     'David Wilson', 'Lisa Anderson', 'James Taylor', 'Maria Garcia',
  //     'Robert Martinez', 'Jennifer Lee', 'William White', 'Patricia Harris',
  //     'Richard Clark', 'Linda Lewis', 'Thomas Walker'
  //   ];
  //   
  //   // Generate 2-3 employees near each QR location (checkpoints only, not start/end)
  //   route.forEach((qrLocation, qrIndex) => {
  //     // Only generate employees near checkpoints (not start/end points)
  //     if (qrLocation.type === 'checkpoint') {
  //       const numEmployees = Math.floor(Math.random() * 2) + 2; // 2-3 employees per checkpoint
  //       
  //       for (let i = 0; i < numEmployees; i++) {
  //         // Generate random offset from QR location (within ~50 meters)
  //         const latOffset = (Math.random() - 0.5) * 0.0005; // ~50 meters
  //         const lngOffset = (Math.random() - 0.5) * 0.0005; // ~50 meters
  //         
  //         const employeeIndex = (qrIndex * numEmployees + i) % employeeNames.length;
  //         
  //         employees.push({
  //           id: `emp_${qrIndex}_${i}`,
  //           name: employeeNames[employeeIndex],
  //           lat: qrLocation.latitude + latOffset,
  //           lng: qrLocation.longitude + lngOffset,
  //           nearQR: qrLocation.qr_code || `QR-${qrIndex}`,
  //           status: Math.random() > 0.3 ? 'On Duty' : 'Off Duty',
  //           role: 'Security Officer',
  //         });
  //       }
  //     }
  //   });
  //   
  //   return employees;
  // }, []);

  // Generate patrol routes from real QR location data (replaces mock data)
  // All routes are always displayed on the map
  useEffect(() => {
    if (livePatrols.length > 0 && qrLookup && Object.keys(qrLookup).length > 0) {
      const routes = livePatrols.map((patrol, index) => {
        const patrolId = patrol.id;
        
        // Check if we already have a route for this patrol
        let route;
        if (persistentRoutesRef.current.has(patrolId)) {
          // Use existing route
          route = persistentRoutesRef.current.get(patrolId);
          console.log(`🗺️ Using cached route for patrol ${patrolId}`);
        } else {
          // Build route from real QR location data
          // Prefer qr_sequence if provided; fallback to start/end only
          const sequence = Array.isArray(patrol.qr_sequence) && patrol.qr_sequence.length > 0
            ? patrol.qr_sequence
            : [patrol.start_qr, patrol.end_qr].filter(Boolean);
          
          // Build route from QR locations
          route = sequence
            .map((qrId) => {
              const qrInfo = qrLookup[qrId] || {};
              const latitude = qrInfo.latitude;
              const longitude = qrInfo.longitude;
              
              if (typeof latitude !== 'number' || typeof longitude !== 'number') {
                return null; // skip if coordinates unavailable
              }

              return {
                latitude,
                longitude,
                qr_code: qrId,
                location_name: qrInfo.name || qrId,
              };
            })
            .filter(Boolean);

          // Only cache if we have valid route points
          if (route.length > 0) {
          persistentRoutesRef.current.set(patrolId, route);
            console.log(`🗺️ Built and cached route from ${route.length} QR locations for patrol ${patrolId}`);
          } else {
            console.warn(`⚠️ No valid QR locations found for patrol ${patrolId}`);
            route = []; // Empty route if no valid QR locations
          }

          // COMMENTED OUT: Mock employee generation
          // const employees = generateDummyEmployeesNearQR(route);
          // persistentEmployeesRef.current.set(patrolId, employees);
          // console.log(`👥 Generated and cached ${employees.length} employees for patrol ${patrolId}`);
        }
        
        return {
          patrol_id: patrolId,
          patrol_name: patrol.siteName || `Patrol ${index + 1}`,
          route: route,
          color: getPatrolColor(index),
        };
      });
      
      setAllPatrolRoutes(routes);
      console.log('🗺️ Total patrol routes ready to display:', routes.length);
    } else {
      setAllPatrolRoutes([]);
      if (livePatrols.length === 0) {
      console.log('🗺️ No patrols to display');
      } else if (!qrLookup || Object.keys(qrLookup).length === 0) {
        console.log('🗺️ Waiting for QR location data...');
    }
    }
  }, [livePatrols, qrLookup, getPatrolColor]);

  // Update patrol employees when a patrol is selected
  // COMMENTED OUT: Using real employee data from props instead of mock data
  // useEffect(() => {
  //   if (selectedPatrolForMap) {
  //     // Get employees for the selected patrol from cache
  //     const employees = persistentEmployeesRef.current.get(selectedPatrolForMap);
  //     if (employees) {
  //       setPatrolEmployees(employees);
  //       console.log(`👥 Displaying ${employees.length} employees for selected patrol ${selectedPatrolForMap}`);
  //     } else {
  //       setPatrolEmployees([]);
  //       console.log(`👥 No employees found for patrol ${selectedPatrolForMap}`);
  //     }
  //   } else {
  //     // Clear employees when no patrol is selected
  //     setPatrolEmployees([]);
  //     console.log('👥 Cleared patrol employees (no patrol selected)');
  //   }
  // }, [selectedPatrolForMap]);
  
  // Use real employee data from employees prop instead of mock data
  useEffect(() => {
    if (selectedPatrolForMap) {
      const patrol = livePatrols.find(p => p.id === selectedPatrolForMap);
      if (patrol && patrol.patrol_officers && Array.isArray(patrol.patrol_officers)) {
        // Filter employees that are part of this patrol
        const patrolEmployeeIds = patrol.patrol_officers.map(po => po.user_id);
        const patrolEmployeeData = employees.filter(emp => 
          patrolEmployeeIds.includes(emp.id)
        );
        setPatrolEmployees(patrolEmployeeData);
        console.log(`👥 Displaying ${patrolEmployeeData.length} real employees for selected patrol ${selectedPatrolForMap}`);
      } else {
        setPatrolEmployees([]);
        console.log(`👥 No employees found for patrol ${selectedPatrolForMap}`);
      }
    } else {
      // Clear employees when no patrol is selected
      setPatrolEmployees([]);
      console.log('👥 Cleared patrol employees (no patrol selected)');
    }
  }, [selectedPatrolForMap, livePatrols, employees]);

  // Build QR path for the currently selected patrol (uses qrLookup with coordinates)
  useEffect(() => {
    if (!selectedPatrolForMap || !qrLookup || Object.keys(qrLookup).length === 0) {
      setPatrolQRLocations([]);
      return;
      }
      
    const patrol = livePatrols.find(p => p.id === selectedPatrolForMap);
    if (!patrol) {
      setPatrolQRLocations([]);
        return;
      }

    // Prefer qr_sequence if provided; fallback to start/end only
    const sequence = Array.isArray(patrol.qr_sequence) && patrol.qr_sequence.length > 0
      ? patrol.qr_sequence
      : [patrol.start_qr, patrol.end_qr].filter(Boolean);

    const pathLocations = sequence
      .map((qrId, idx) => {
        const qrInfo = qrLookup[qrId] || {};
        const latitude = qrInfo.latitude;
        const longitude = qrInfo.longitude;
        if (typeof latitude !== 'number' || typeof longitude !== 'number') {
          return null; // skip if coordinates unavailable
        }

        const isFirst = idx === 0;
        const isLast = idx === sequence.length - 1;

        return {
          qr_code: qrId,
          location_name: qrInfo.name || qrId,
          latitude,
          longitude,
          type: isFirst ? 'start' : (isLast ? 'end' : 'checkpoint'),
          checkpoint_number: !isFirst && !isLast ? idx + 1 : null, // Add 1 so checkpoints start from 2 (after Start Point 1)
          scanned: false, // no scan data available in this view
          scan_timestamp: null,
        };
      })
      .filter(Boolean);

    setPatrolQRLocations(pathLocations);
  }, [selectedPatrolForMap, livePatrols, qrLookup]);

  // Display drivable route between QR points for the selected patrol using Directions Service
  useEffect(() => {
    // If no patrol selected or not enough QR points, clear any existing route and exit
    if (!selectedPatrolForMap || !mapComponentRef.current || !patrolQRLocations || patrolQRLocations.length < 2) {
      if (mapComponentRef.current?.clearRoute) {
        mapComponentRef.current.clearRoute();
      }
      return;
    }

    // Validate coordinates
    const hasValidCoords = (loc) =>
      loc &&
      typeof loc.latitude === 'number' &&
      isFinite(loc.latitude) &&
      typeof loc.longitude === 'number' &&
      isFinite(loc.longitude);

    // Prepare origin, destination, and waypoints from QR locations
    const [start, ...rest] = patrolQRLocations;
    const destination = rest.length > 0 ? rest[rest.length - 1] : null;
    const checkpoints = rest.slice(0, Math.max(rest.length - 1, 0));

    if (!hasValidCoords(start) || !destination || !hasValidCoords(destination)) {
      console.warn('⚠️ Cannot draw patrol route: missing start/end coordinates');
      if (mapComponentRef.current?.clearRoute) {
        mapComponentRef.current.clearRoute();
      }
      return;
    }

    const waypoints = checkpoints
      .filter(hasValidCoords)
      .map((loc) => ({
        lat: loc.latitude,
        lng: loc.longitude,
        address: loc.location_name || loc.qr_code,
      }));

    const origin = { lat: start.latitude, lng: start.longitude };
    const destinationLatLng = { lat: destination.latitude, lng: destination.longitude };

    const drawRoute = async () => {
      if (!mapComponentRef.current?.calculateRoute) return;

      try {
        // Add checkpoint markers as halts (for consistent UX with LiveRides)
        if (mapComponentRef.current?.addHaltMarkers) {
          const halts = checkpoints
            .filter(hasValidCoords)
            .map((loc, idx) => ({
              address: loc.location_name || loc.qr_code || `Checkpoint ${idx + 1}`,
              coordinates: { latitude: loc.latitude, longitude: loc.longitude },
            }));
          mapComponentRef.current.addHaltMarkers(halts);
        }

        await mapComponentRef.current.calculateRoute(origin, destinationLatLng, waypoints);
        console.log(`🚗 Drew drivable route for patrol ${selectedPatrolForMap} with ${patrolQRLocations.length} QR points`);
      } catch (error) {
        console.error('Error drawing patrol route with Directions Service:', error);
        if (mapComponentRef.current?.clearRoute) {
          mapComponentRef.current.clearRoute();
        }
      }
    };

    // Wait for map to be fully initialized
    const timer = setTimeout(() => {
      drawRoute();
    }, 600); // Slight delay for map readiness

    // Cleanup on dependency change
    return () => {
      clearTimeout(timer);
      if (mapComponentRef.current?.clearRoute) {
        mapComponentRef.current.clearRoute();
      }
    };
  }, [selectedPatrolForMap, patrolQRLocations]);

  // Note: Officer location tracking has been removed as it requires patrol-specific APIs
  // This functionality should be implemented when patrol location tracking APIs are available
  const fetchAndDisplayOfficerLocation = useCallback(async () => {
    if (!selectedOfficer || !mapComponentRef.current) return;

    console.log('📍 Officer location tracking not yet implemented for patrols');
    
    // Clear any existing officer tracking
    if (mapComponentRef.current.clearDriverTracking) {
      mapComponentRef.current.clearDriverTracking();
    }
    if (mapComponentRef.current.clearConvoyDriverMarkers) {
      mapComponentRef.current.clearConvoyDriverMarkers();
    }
  }, [selectedOfficer]);

  // Note: Officer location markers have been removed as they require patrol-specific APIs
  // This functionality should be implemented when patrol location tracking APIs are available
  const fetchAndDisplayPatrolOfficerMarkers = useCallback(async () => {
    if (!selectedPatrolForMap || !mapComponentRef.current) {
      // Clear markers when no patrol is selected
      if (mapComponentRef.current && mapComponentRef.current.clearConvoyDriverMarkers) {
        mapComponentRef.current.clearConvoyDriverMarkers();
      }
      return;
    }

    // Don't show patrol markers if a specific officer is selected
    if (selectedOfficer) {
      return;
    }

    console.log('📍 Patrol officer location markers not yet implemented for patrols');

    // Clear any existing markers
    if (mapComponentRef.current.clearConvoyDriverMarkers) {
      mapComponentRef.current.clearConvoyDriverMarkers();
    }
  }, [selectedPatrolForMap, selectedOfficer]);

  // Display officer location (with path) when officer is selected (no auto-refresh)
  useEffect(() => {
    if (selectedOfficer && mapComponentRef.current && selectedPatrolForMap) {
      // Fetch and display officer location once when selected (no periodic updates)
      fetchAndDisplayOfficerLocation();

      // Cleanup: clear tracking when officer is deselected or component unmounts
      return () => {
        if (mapComponentRef.current && mapComponentRef.current.clearDriverTracking) {
          mapComponentRef.current.clearDriverTracking();
        }
      };
    } else {
      // Clear officer tracking when no officer is selected OR when patrol is deselected
      if (mapComponentRef.current && mapComponentRef.current.clearDriverTracking) {
        mapComponentRef.current.clearDriverTracking();
      }
      // When officer is deselected but patrol is still selected, show patrol markers again
      if (selectedPatrolForMap && !selectedOfficer) {
        fetchAndDisplayPatrolOfficerMarkers();
      }
    }
  }, [selectedOfficer, fetchAndDisplayOfficerLocation, selectedPatrolForMap, fetchAndDisplayPatrolOfficerMarkers]);

  // Fetch and display patrol officer markers when patrol is selected (no auto-refresh)
  useEffect(() => {
    if (selectedPatrolForMap && !selectedOfficer) {
      // Fetch patrol officer markers when a patrol is selected (no periodic updates)
      fetchAndDisplayPatrolOfficerMarkers();

      return () => {
        // Clear patrol markers when patrol is deselected
        if (mapComponentRef.current && mapComponentRef.current.clearConvoyDriverMarkers) {
          mapComponentRef.current.clearConvoyDriverMarkers();
        }
      };
    } else if (!selectedPatrolForMap) {
      // Clear patrol markers when no patrol is selected
      if (mapComponentRef.current && mapComponentRef.current.clearConvoyDriverMarkers) {
        mapComponentRef.current.clearConvoyDriverMarkers();
      }
    }
  }, [selectedPatrolForMap, selectedOfficer, fetchAndDisplayPatrolOfficerMarkers]);

  // Fetch patroller live locations for the selected patrol and render markers on the map
  useEffect(() => {
    let isCancelled = false;

    const clearPatrollerMarkers = () => {
      if (mapComponentRef.current?.clearConvoyDriverMarkers) {
        mapComponentRef.current.clearConvoyDriverMarkers();
      }
    };

    const fetchPatrollerLocations = async (patrolId) => {
      try {
        const mapData = await patrollingLocationApi.getPatrolMapData(patrolId);
        const locations = (mapData?.patroller_locations || [])
          .map((loc) => {
            const employee = employees.find(emp => emp.id === loc.user_id);
            const latitude = typeof loc.latitude === 'number' ? loc.latitude : parseFloat(loc.latitude);
            const longitude = typeof loc.longitude === 'number' ? loc.longitude : parseFloat(loc.longitude);
            if (!isFinite(latitude) || !isFinite(longitude)) {
              return null;
            }

            return {
              latitude,
              longitude,
              driverName: employee?.name || loc.user_id || 'Patroller',
              timestamp: loc.timestamp,
            };
          })
          .filter(Boolean);

        if (isCancelled) return;

        setPatrolLiveLocations(locations);

        // Display markers on the map using existing convoy marker utility
        if (mapComponentRef.current?.displayDriverMarkersOnly) {
          mapComponentRef.current.displayDriverMarkersOnly(locations, employeeIcon);
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('Error fetching patroller locations:', err);
          setPatrolLiveLocations([]);
          clearPatrollerMarkers();
        }
      }
    };

    if (!selectedPatrolForMap) {
      setPatrolLiveLocations([]);
      clearPatrollerMarkers();
      return;
    }

    const selectedPatrol = livePatrols.find(p => p.id === selectedPatrolForMap);
    const patrolId = selectedPatrol?.patrol_id || selectedPatrol?.id;

    if (patrolId) {
      fetchPatrollerLocations(patrolId);
    } else {
      clearPatrollerMarkers();
    }

    return () => {
      isCancelled = true;
      clearPatrollerMarkers();
    };
  }, [selectedPatrolForMap, livePatrols, employees]);

  // Debug log for loaded patroller locations (helps track data flow and avoids unused state)
  useEffect(() => {
    if (!selectedPatrolForMap) return;
    console.log(
      `📍 PatrollingDashboard: Loaded ${patrolLiveLocations.length} patroller locations for patrol ${selectedPatrolForMap}`
    );
  }, [patrolLiveLocations, selectedPatrolForMap]);

  // Fetch live patrols based on API logic
  const fetchLivePatrols = useCallback(async (forceRefresh = false) => {
    try {
      const now = new Date();
      
      // Helper function to get site name with caching
      const getSiteName = async (siteId) => {
        if (!siteId || siteId.trim() === '') {
          return 'Unknown';
        }
        
        // Check convoy details cache first (shared with PatrolDetails)
        const cachedName = convoyDetailsCache.getSite(siteId);
        if (cachedName) {
          return cachedName;
        }
        
        try {
          const siteData = await getSiteDetails(siteId);
          const siteName = siteData?.site_name || 'Unknown';
          convoyDetailsCache.setSite(siteId, siteName);
          return siteName;
        } catch (error) {
          console.error(`Error fetching site details for ${siteId}:`, error);
          return 'Unknown';
        }
      };

      // Fetch all QR codes with pagination support (include coordinates for map path)
      let qrLookupMap = {};
      try {
        console.log('🔄 Fetching QR codes for lookup...');
        let hasMore = true;
        let lastKey = null;
        let totalFetched = 0;

        // Fetch all pages of QR codes
        while (hasMore) {
          const options = lastKey ? { last_key: lastKey, limit: 100 } : { limit: 100 };
          const qrResponse = await qrApi.getAll(options);
          const qrItems = qrResponse.items || [];
          
          // Create a map of QR IDs to QR details with coordinates
          qrItems.forEach(qr => {
            if (qr.qr_id) {
              qrLookupMap[qr.qr_id] = {
                name: qr.name || qr.landmark || qr.qr_id,
                address: qr.landmark || qr.address || 'No address',
                latitude: qr.location?.latitude ?? null,
                longitude: qr.location?.longitude ?? null,
              };
            }
          });
          
          totalFetched += qrItems.length;
          
          // Check if there are more pages
          hasMore = qrResponse.has_more || false;
          lastKey = qrResponse.last_key || null;
          
          console.log(`📦 Fetched ${qrItems.length} QR codes (Total: ${totalFetched})`);
        }
        
        console.log(`✅ Loaded ${Object.keys(qrLookupMap).length} QR codes`);
        setQrLookup(qrLookupMap); // Store in state for use in other components
      } catch (error) {
        console.error('Error fetching QR codes:', error);
      }

      // Get today's date in YYYY-MM-DD format
      const todayDate = now.toISOString().split('T')[0];
      console.log("🔍 Debug: Fetching live patrols for date:", todayDate);

      // Check cache first if not forcing refresh
      if (!forceRefresh) {
        const cachedPatrols = dashboardCache.getLiveRides(todayDate); // Reuse cache structure
        if (cachedPatrols) {
          console.log('📦 Using cached live patrols');
          setLivePatrols(cachedPatrols);
          return;
        }
      } else {
        // Clear cache on force refresh
        dashboardCache.clearLiveRides();
      }

      // Fetch patrols using the real API
      // Filter by date to get today's patrols
      const apiResponse = await patrollingApi.getAll({ 
        date: todayDate,
        limit: 100 // Get up to 100 patrols
      });
      
      const allPatrols = apiResponse.patrols || [];
      console.log("🔍 Debug: Fetched from API -", allPatrols.length, "total patrols for today");

      const livePatrolsList = [];

      // Process each patrol to check if it's currently ongoing
      for (const patrol of allPatrols) {
        // Extract site_id from tenant_id (format: tenant-id/site-id) or use patrol.site_id
        let siteId = '';
        if (patrol.tenant_id && patrol.tenant_id.includes('/')) {
          const parts = patrol.tenant_id.split('/');
          siteId = parts[1] || '';
        } else if (patrol.site_id) {
          siteId = patrol.site_id;
        }

        // Get timestamps in ISO format
        const startTimestampStr = patrol.patrol_start_timestamp;
        const endTimestampStr = patrol.patrol_end_timestamp;

        console.log(
          "🔍 Debug: Processing patrol - Start:", startTimestampStr, 
          "End:", endTimestampStr
        );

        if (!startTimestampStr || !endTimestampStr) {
          console.log("⚠️ Debug: Skipping patrol due to missing timestamps");
          continue;
        }

        try {
          // Parse ISO timestamps to Date objects
          const startTime = new Date(startTimestampStr);
          const endTime = new Date(endTimestampStr);

          // Ensure all times are in UTC for accurate comparison
          const nowUtc = new Date(now.toISOString());
          const startTimeUtc = new Date(startTime.toISOString());
          const endTimeUtc = new Date(endTime.toISOString());

          // Add a small buffer (2 minutes) to account for potential clock differences
          const nowWithBuffer = new Date(nowUtc.getTime() + 2 * 60 * 1000); // +2 minutes
          const startWithBuffer = new Date(startTimeUtc.getTime() - 2 * 60 * 1000); // -2 minutes

          console.log(
            "🔍 Debug: Parsed times - Start:", startTimeUtc, 
            "End:", endTimeUtc, 
            "Now:", nowUtc
          );

          // Check if the patrol is ongoing based on status or time
          const isAfterStart = nowWithBuffer.getTime() > startWithBuffer.getTime();
          const isBeforeEnd = nowUtc.getTime() < endTimeUtc.getTime();
          const isOngoing = (isAfterStart && isBeforeEnd) || patrol.status === 'in_progress';

          if (isOngoing) {
            console.log("✅ Debug: Found ongoing patrol!");

            // Extract user_ids from patrol_details
            const patrolOfficers = [];
            if (patrol.patrol_details && Array.isArray(patrol.patrol_details)) {
              patrol.patrol_details.forEach((detail, index) => {
                if (detail.user_ids && Array.isArray(detail.user_ids)) {
                  detail.user_ids.forEach(userId => {
                    patrolOfficers.push({
                      user_id: userId,
                      vehicle_id: detail.vehicle_id || null,
                      material: detail.material || null,
                      notes: detail.notes ||detail.note || null,
                    });
                  });
                }
              });
            }

            // Fetch site name (no supervisor in new API structure)
            const siteName = await getSiteName(siteId);

            // Enrich patrol with QR names
            const startQrId = patrol.start_qr_id || patrol.start_qr;
            const endQrId = patrol.end_qr_id || patrol.end_qr;
            
            const startQrName = startQrId && qrLookupMap[startQrId] 
              ? qrLookupMap[startQrId].name 
              : startQrId || 'N/A';
            const startQrAddress = startQrId && qrLookupMap[startQrId] 
              ? qrLookupMap[startQrId].address 
              : null;
            
            const endQrName = endQrId && qrLookupMap[endQrId] 
              ? qrLookupMap[endQrId].name 
              : endQrId || 'N/A';
            const endQrAddress = endQrId && qrLookupMap[endQrId] 
              ? qrLookupMap[endQrId].address 
              : null;

            // Create patrol object matching the expected format
            livePatrolsList.push({
              id: patrol.patrol_id || `patrol_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
              patrol_id: patrol.patrol_id, // Keep original patrol_id for API calls
              patrol_name: patrol.patrol_name || 'Patrol', // Include patrol name from API
              status: patrol.status || 'in progress',
              site_id: siteId,
              siteName: siteName,
              supervisorName: null, // No supervisor in new API structure
              patrol_officers: patrolOfficers, // Include patrol officers with vehicle, material, notes
              patrol_details: patrol.patrol_details || [], // Keep original patrol_details for table display
              patrol_start_timestamp: startTimestampStr,
              patrol_end_timestamp: endTimestampStr,
              start_qr: startQrId || 'N/A', // Include Start QR ID
              start_qr_name: startQrName, // Include Start QR name
              start_qr_address: startQrAddress, // Include Start QR address
              end_qr: endQrId || 'N/A', // Include End QR ID
              end_qr_name: endQrName, // Include End QR name
              end_qr_address: endQrAddress, // Include End QR address
              qr_sequence: patrol.qr_sequence || [], // Include QR sequence
              patrol_type: patrol.patrol_type || 'on_foot', // Include patrol type
            });
          } else {
            console.log("⏰ Debug: Patrol is not currently ongoing");
          }
        } catch (parseError) {
          console.error("❌ Debug: Error parsing timestamps:", parseError);
          continue;
        }
      }

      console.log("🔍 Debug: Final result -", livePatrolsList.length, "ongoing patrols found");

      // Cache the live patrols list
      dashboardCache.setLiveRides(todayDate, livePatrolsList);

      setLivePatrols(livePatrolsList);
    } catch (error) {
      console.error("❌ Debug: Error fetching live patrols:", error);
      setError(error.message || 'Failed to load live patrols');
      // Don't clear existing patrols on error, just log it
    }
  }, []);

  // Effect to handle responsive layout on mobile screens
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      // Mobile breakpoint: < 768px
      const isMobile = width < 768;
      setIsMobileViewport(isMobile);
      setViewportWidth(width);
    };

    // Check on mount
    handleResize();

    // Listen to resize events
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!isAuthenticated) {
        console.log('PatrollingDashboard: User not authenticated, skipping API calls');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        // Check cache first for sites and users
        let allSitesResponse = dashboardCache.getAllSites();
        let allUsersResponse = dashboardCache.getAllUsers();
        
        // Fetch if not cached
        if (!allSitesResponse || !allUsersResponse) {
          const [fetchedSites, fetchedUsers] = await Promise.all([
            allSitesResponse ? Promise.resolve(allSitesResponse) : dashboardApi.getAllSites(),
            allUsersResponse ? Promise.resolve(allUsersResponse) : dashboardApi.getAllUsersWithDutyData(),
          ]);
          
          if (!allSitesResponse) {
            allSitesResponse = fetchedSites;
            dashboardCache.setAllSites(fetchedSites);
          }
          
          if (!allUsersResponse) {
            allUsersResponse = fetchedUsers;
            dashboardCache.setAllUsers(fetchedUsers);
          }
        }

        // Process sites data
        const sitesArray = Array.isArray(allSitesResponse) ? allSitesResponse : [];
        const fetchedOfficeLocations = sitesArray.map(site => {
          let lat = 0;
          let lng = 0;
          let geofenceDetails = null;

          try {
            const parsedDetails = JSON.parse(site.geofence_details);
            geofenceDetails = parsedDetails;

            if (parsedDetails.type === 'Polygon' && parsedDetails.coordinates && parsedDetails.coordinates.length > 0) {
              lat = parsedDetails.coordinates[0].latitude;
              lng = parsedDetails.coordinates[0].longitude;
            } else if (parsedDetails.type === 'Circle' && parsedDetails.center) {
              lat = parsedDetails.center.latitude;
              lng = parsedDetails.center.longitude;
            } else {
              lat = 20.5937;
              lng = 78.9629;
            }
          } catch (parseError) {
            console.error(`Error parsing geofence_details for site ${site.site_id}:`, parseError);
            lat = 20.5937;
            lng = 78.9629;
          }

          return {
            id: site.site_id,
            name: site.site_name,
            address: site.address,
            lat: lat,
            lng: lng,
            geofenceDetails: geofenceDetails,
            radius: geofenceDetails?.radius || 500,
          };
        });
        
        // Store all office locations (unfiltered)
        setAllOfficeLocations(fetchedOfficeLocations);

        // Process employees data
        const allUsersArray = allUsersResponse && allUsersResponse.users ? allUsersResponse.users : [];
        const fetchedEmployees = await Promise.all(
          allUsersArray.map(async user => {
            let imageUrl = `https://placehold.co/100x100/aabbcc/ffffff?text=${user.user_name ? user.user_name.charAt(0) : '?'}`;
            if (user.image_path) {
              try {
                imageUrl = await dashboardApi.getPresignedS3Url(user.image_path);
                if (!imageUrl) {
                  imageUrl = `https://placehold.co/100x100/ff0000/ffffff?text=Error`;
                }
              } catch (imgError) {
                console.error(`Failed to get presigned URL for ${user.user_name}:`, imgError);
                imageUrl = `https://placehold.co/100x100/ff0000/ffffff?text=Error`;
              }
            }
            return {
              id: user.user_id,
              name: user.user_name,
              role: user.role_name,
              image: imageUrl,
              officeId: user.site_id || 'N/A',
              status: user.on_duty ? 'On Duty' : 'Off Duty',
              note: user.spec_role || 'N/A',
              lat: user.last_location?.latitude,
              lng: user.last_location?.longitude,
            };
          })
        );
        
        setEmployees(fetchedEmployees);
        
        // Fetch live patrols after sites and employees are loaded
        await fetchLivePatrols();
        
      } catch (err) {
        console.error('Error fetching initial data:', err);
        setError(err.message || 'Failed to load data.');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchInitialData();
    }
  }, [isAuthenticated, fetchLivePatrols]);

  // Callback for getting selected patrol route
  const getSelectedPatrolRoute = useCallback(() => {
    if (!selectedPatrolForMap) return null;
    return livePatrols.find(patrol => patrol.id === selectedPatrolForMap);
  }, [selectedPatrolForMap, livePatrols]);

  // Callback for showing patrol info table
  const handleShowPatrolInfoTable = useCallback(async (patrolList) => {
    try {
      // Check if it's patrol officer data
      const isPatrolOfficerData = Array.isArray(patrolList) && patrolList.length > 0 && 
                           (patrolList[0].serialNumber !== undefined || patrolList[0].userId !== undefined);
      
      if (!isPatrolOfficerData || !Array.isArray(patrolList) || patrolList.length === 0) {
        // If no patrol officer data, use existing logic
        const dataToShow = Array.isArray(patrolList) && patrolList.length > 0 
          ? patrolList 
          : livePatrols;
        const dataArray = Array.isArray(dataToShow) ? dataToShow : [];
        setAllPatrolTableData(dataArray); // Store all data
        setPatrolTableData(dataArray); // Set initial display data
        setSelectedGroupIndex(null); // Reset filter
        setShowPatrolInfoTable(true);
        return;
      }

      // Show loading state
      setLoadingTableData(true);
      setShowPatrolInfoTable(true);

      // Fetch detailed information for each patrol officer
      const enrichedData = await Promise.all(
        patrolList.map(async (patrolOfficer, index) => {
          const serialNumber = patrolOfficer.serialNumber || index + 1;
          const userId = patrolOfficer.userId || patrolOfficer.user_id || 'N/A';
          const patrolId = patrolOfficer.patrolId || patrolOfficer.patrol_id || 'N/A';
          const siteId = patrolOfficer.siteId || patrolOfficer.site_id;

          // Initialize default values
          let officerName = "Unknown Officer";
          let driverName = "N/A";
          let siteName = "Unknown Site";
          let statusMessage = "Unknown Status";
          let vehicleId = patrolOfficer.vehicle_id || 'N/A';
          let material = patrolOfficer.material || 'N/A';
          let notes = patrolOfficer.notes || 'N/A';
          let groupIndex = patrolOfficer.groupIndex !== undefined ? patrolOfficer.groupIndex : null; // Preserve group index from sidebar

          // Use officer name from patrolOfficer data
          if (userId && userId !== 'N/A') {
            officerName = patrolOfficer.officerName || "Unknown Officer";
          }
          
          // If groupIndex is not provided, try to find it from patrol_details
          if (groupIndex === null) {
            const patrol = livePatrols.find(p => 
              (p.patrol_id === patrolId || p.id === patrolId) && 
              p.patrol_details && 
              Array.isArray(p.patrol_details)
            );
            
            if (patrol && patrol.patrol_details) {
              // Find which group (patrol_detail index) this officer belongs to
              for (let i = 0; i < patrol.patrol_details.length; i++) {
                const detail = patrol.patrol_details[i];
                if (detail.user_ids && Array.isArray(detail.user_ids) && detail.user_ids.includes(userId)) {
                  groupIndex = i;
                  // Also update vehicleId, material, notes from the group if not already set
                  if (vehicleId === 'N/A' && detail.vehicle_id) {
                    vehicleId = detail.vehicle_id;
                  }
                  if (material === 'N/A' && detail.material) {
                    material = detail.material;
                  }
                  if (notes === 'N/A' && (detail.notes || detail.note)) {
                    notes = detail.notes || detail.note || 'N/A';
                  }
                  break;
                }
              }
            }
          }

          // Identify driver if vehicle is assigned
          if (vehicleId && vehicleId !== 'N/A') {
            // Find the patrol from livePatrols to get patrol_details
            const patrol = livePatrols.find(p => 
              (p.patrol_id === patrolId || p.id === patrolId) && 
              p.patrol_details && 
              Array.isArray(p.patrol_details)
            );
            
            if (patrol) {
              // Find the patrol_detail that matches this vehicle
              const patrolDetail = patrol.patrol_details.find(detail => 
                detail.vehicle_id === vehicleId
              );
              
              if (patrolDetail && patrolDetail.user_ids && Array.isArray(patrolDetail.user_ids)) {
                // Try to find driver by role first
                let driverId = null;
                for (const uid of patrolDetail.user_ids) {
                  const employee = employees.find(emp => emp.id === uid);
                  if (employee && employee.role && 
                      (employee.role.toLowerCase().includes('driver') || 
                       employee.role.toLowerCase().includes('chauffeur'))) {
                    driverId = uid;
                    break;
                  }
                }
                
                // If no driver role found, assume first user is driver (common pattern)
                if (!driverId && patrolDetail.user_ids.length > 0) {
                  driverId = patrolDetail.user_ids[0];
                }
                
                // Get driver name using driverApi.fetchDriverDetails
                if (driverId) {
                  try {
                    // Check cache first
                    const cachedDriver = driverDetailsCacheRef.current.get(driverId);
                    if (cachedDriver && cachedDriver.user_name) {
                      driverName = cachedDriver.user_name;
                      console.log(`✅ Using cached driver name for ${driverId}: ${driverName}`);
                    } else {
                      // Try to get from employees array first (faster, but may not have driver role)
                      const driverEmployee = employees.find(emp => emp.id === driverId);
                      if (driverEmployee && driverEmployee.name) {
                        driverName = driverEmployee.name;
                        // Cache it
                        driverDetailsCacheRef.current.set(driverId, {
                          user_name: driverEmployee.name,
                          timestamp: Date.now()
                        });
                        console.log(`✅ Using employee array driver name for ${driverId}: ${driverName}`);
                      } else {
                        // Fetch from API using driverApi.fetchDriverDetails
                        console.log(`🔍 Fetching driver details from API for ${driverId}...`);
                        const driverDetails = await driverApi.fetchDriverDetails(driverId);
                        driverName = driverDetails?.user_name || driverDetails?.name || driverId || 'N/A';
                        
                        // Cache the result
                        if (driverDetails && driverDetails.user_name) {
                          driverDetailsCacheRef.current.set(driverId, {
                            user_name: driverDetails.user_name,
                            timestamp: Date.now()
                          });
                          console.log(`✅ Fetched and cached driver name for ${driverId}: ${driverName}`);
                        } else {
                          console.warn(`⚠️ Driver details API response missing user_name for ${driverId}`);
                        }
                      }
                    }
                  } catch (error) {
                    console.error(`❌ Error fetching driver details for ${driverId}:`, error);
                    // Fallback to employee array or driverId
                    const driverEmployee = employees.find(emp => emp.id === driverId);
                    driverName = driverEmployee?.name || driverId || 'N/A';
                    console.log(`⚠️ Using fallback driver name: ${driverName}`);
                  }
                } else {
                  console.warn(`⚠️ No driver ID found for vehicle ${vehicleId} in patrol ${patrolId}`);
                }
              } else {
                console.warn(`⚠️ No patrol_detail found for vehicle ${vehicleId} in patrol ${patrolId}`);
              }
            } else {
              console.warn(`⚠️ Patrol ${patrolId} not found in livePatrols`);
            }
          }

          // Fetch site details
          if (siteId) {
            try {
              // Check cache first
              const cachedName = convoyDetailsCache.getSite(siteId);
              if (cachedName) {
                siteName = cachedName;
              } else {
                const siteResponse = await getSiteDetails(siteId);
                siteName = siteResponse?.site_name || siteResponse?.name || "Unknown Site";
                convoyDetailsCache.setSite(siteId, siteName);
              }
            } catch (error) {
              console.error(`Error fetching site details for ${siteId}:`, error);
              siteName = patrolOfficer.siteName || "Unknown Site";
            }
          }

          // Use patrol status to determine status message
          // Note: Individual officer status tracking requires patrol-specific APIs
          if (patrolId && patrolId !== 'N/A') {
            // Use the patrol's overall status as a fallback
            const patrolStatus = patrolOfficer.status || 'in progress';
            
            if (patrolStatus === 'completed') {
                    statusMessage = "Patrol is successfully done!";
            } else if (patrolStatus === 'in progress') {
                    statusMessage = "Patrol in progress.";
            } else if (patrolStatus === 'not started') {
                    statusMessage = "Patrol is not started.";
                } else {
              statusMessage = "Patrol status unknown";
            }
          }

          // Return enriched data for table display
          return {
            serialNumber: serialNumber,
            userId: userId,
            officerName: officerName,
            driverName: driverName,
            siteName: siteName,
            statusMessage: statusMessage,
            status: statusMessage, // For backward compatibility
            vehicleId: vehicleId,
            material: material,
            notes: notes,
            groupIndex: groupIndex, // Preserve group index to maintain patrol_details structure
          };
        })
      );

      setAllPatrolTableData(enrichedData); // Store all data
      setPatrolTableData(enrichedData); // Set initial display data
      setSelectedGroupIndex(null); // Reset filter when new data loads
      setLoadingTableData(false);
    } catch (error) {
      console.error("Error fetching patrol info table data:", error);
      // Fallback to basic data if fetching fails
      const dataToShow = Array.isArray(patrolList) && patrolList.length > 0 
        ? patrolList 
        : livePatrols;
      const dataArray = Array.isArray(dataToShow) ? dataToShow : [];
      setAllPatrolTableData(dataArray); // Store all data
      setPatrolTableData(dataArray); // Set initial display data
      setSelectedGroupIndex(null); // Reset filter when new data loads
      setLoadingTableData(false);
    }
  }, [livePatrols, employees]);

  // Callback for closing patrol info table
  const handleClosePatrolInfoTable = useCallback(() => {
    setShowPatrolInfoTable(false);
    setPatrolTableData([]);
    setAllPatrolTableData([]);
    setSelectedGroupIndex(null);
  }, []);

  // Extract unique groups from patrol table data based on groupIndex (preserves original patrol_details structure)
  const getGroupsFromTableData = useMemo(() => {
    if (!allPatrolTableData || allPatrolTableData.length === 0) {
      return [];
    }
    
    // Group by groupIndex (which corresponds to patrol_details array index)
    // If groupIndex is null, fall back to grouping by vehicleId
    const groupMap = new Map();
    allPatrolTableData.forEach((row) => {
      const groupKey = row.groupIndex !== null && row.groupIndex !== undefined
        ? `group_${row.groupIndex}` // Use groupIndex as primary key
        : `vehicle_${row.vehicleId || 'N/A'}`; // Fallback to vehicleId
      
      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          groupIndex: row.groupIndex,
          vehicleId: row.vehicleId || 'N/A',
          index: groupMap.size,
          count: 0,
        });
      }
      groupMap.get(groupKey).count++;
    });
    
    // Sort by groupIndex first, then by index
    return Array.from(groupMap.values()).sort((a, b) => {
      if (a.groupIndex !== null && b.groupIndex !== null) {
        return a.groupIndex - b.groupIndex;
      }
      if (a.groupIndex !== null) return -1;
      if (b.groupIndex !== null) return 1;
      return a.index - b.index;
    });
  }, [allPatrolTableData]);

  // Filter table data based on selected group
  useEffect(() => {
    if (selectedGroupIndex === null) {
      // Show all data
      setPatrolTableData(allPatrolTableData);
    } else {
      // Filter by selected group (using groupIndex if available, otherwise vehicleId)
      const selectedGroup = getGroupsFromTableData[selectedGroupIndex];
      if (selectedGroup) {
        const filtered = allPatrolTableData.filter(row => {
          // Match by groupIndex if available, otherwise match by vehicleId
          if (selectedGroup.groupIndex !== null && row.groupIndex !== null) {
            return row.groupIndex === selectedGroup.groupIndex;
          }
          return (row.vehicleId || 'N/A') === selectedGroup.vehicleId;
        });
        setPatrolTableData(filtered);
      } else {
        setPatrolTableData(allPatrolTableData);
      }
    }
  }, [selectedGroupIndex, allPatrolTableData, getGroupsFromTableData]);

  // Handler for dispatching new patrols
  const handleDispatchNewPatrol = patrol => {
    console.log('Dispatching new patrol:', patrol);
    // Implement actual dispatch logic here
  };

  // Compute filtered office locations based on live patrols
  const filteredOfficeLocations = useMemo(() => {
    // If no live patrols, return empty array (no sites to show)
    if (!livePatrols.length || !allOfficeLocations.length) {
      return [];
    }
    
    // Extract all unique site IDs from live patrols
    const siteIdsInLivePatrols = new Set();
    livePatrols.forEach(patrol => {
      if (patrol.site_id) {
        siteIdsInLivePatrols.add(patrol.site_id);
      }
    });
    
    // If a patrol is selected, filter to show only that patrol's site
    if (selectedPatrolForMap) {
      const selectedPatrol = livePatrols.find(patrol => patrol.id === selectedPatrolForMap);
      if (selectedPatrol) {
        const siteId = selectedPatrol.site_id;
        
        return allOfficeLocations.filter(site => 
          site.id === siteId
        );
      }
    }
    
    // If no patrol is selected, show all sites that have live patrols
    return allOfficeLocations.filter(site => 
      siteIdsInLivePatrols.has(site.id)
    );
  }, [selectedPatrolForMap, livePatrols, allOfficeLocations]);

  // Update officeLocations when filtered list changes
  useEffect(() => {
    setOfficeLocations(filteredOfficeLocations);
  }, [filteredOfficeLocations]);

  // Adjust map to show the selected patrol's site or all patrols
  useEffect(() => {
    if (!selectedPatrolForMap) {
      // Clear route if no patrol is selected
      if (mapComponentRef.current && mapComponentRef.current.clearRoute) {
        mapComponentRef.current.clearRoute();
      }
      // Clear selected officer when going back to main view
      if (selectedOfficer) {
        setSelectedOfficer(null);
      }
      // Clear all officer tracking markers when no patrol is selected
      if (mapComponentRef.current && mapComponentRef.current.clearDriverTracking) {
        mapComponentRef.current.clearDriverTracking();
      }
      if (mapComponentRef.current && mapComponentRef.current.clearConvoyDriverMarkers) {
        mapComponentRef.current.clearConvoyDriverMarkers();
      }
      
      // Adjust map to show all patrol routes when no specific patrol is selected
      if (allOfficeLocations.length > 0 && livePatrols.length > 0) {
        const timer = setTimeout(() => {
          if (!mapComponentRef.current) return;
          
          // Calculate bounds to fit all patrol sites
          const bounds = new window.google.maps.LatLngBounds();
          livePatrols.forEach(patrol => {
            const site = allOfficeLocations.find(s => s.id === patrol.site_id);
            if (site) {
              bounds.extend({ lat: site.lat, lng: site.lng });
            }
          });
          
          // Fit map to show all patrol sites
          if (mapComponentRef.current.fitBounds) {
            try {
              mapComponentRef.current.fitBounds(bounds);
            } catch (error) {
              console.error('Error fitting bounds:', error);
            }
          }
        }, 800);
        
        return () => clearTimeout(timer);
      }
      
      return;
    }

    if (!mapComponentRef.current || filteredOfficeLocations.length < 1) {
      return;
    }

    // Find the selected patrol to get site ID
    const selectedPatrol = livePatrols.find(patrol => patrol.id === selectedPatrolForMap);
    if (!selectedPatrol) {
      return;
    }

    // Wait for map to be fully initialized
    const timer = setTimeout(async () => {
      if (!mapComponentRef.current) return;

      // Find site from filtered locations
      const site = filteredOfficeLocations.find(s => s.id === selectedPatrol.site_id);

      if (site && mapComponentRef.current.panToAndZoom) {
        try {
          // Center on the site with appropriate zoom
          mapComponentRef.current.panToAndZoom(
            { lat: site.lat, lng: site.lng },
            14
          );
        } catch (error) {
          console.error('Error panning to site:', error);
        }
      }
    }, 800); // Wait for map to be fully ready
    
    return () => clearTimeout(timer);
  }, [selectedPatrolForMap, filteredOfficeLocations, livePatrols, allOfficeLocations, selectedOfficer]);


  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            Authentication Required
          </h2>
          <p className="text-gray-700 mb-4">
            You need to be logged in to access the Patrolling dashboard.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-700">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-400 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading Patrolling Dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-red-600">
        <div className="text-center">
          <p>Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full flex flex-col">
      {/* Main Content Area - Map and Sidebar Layout */}
      {/* Mobile: flex-col (vertical), Desktop: flex-row (horizontal) */}
      <div className={`flex flex-1 w-full overflow-hidden gap-4 ${isMobileViewport ? 'flex-col' : 'flex-row'}`}>
        {/* Map Section */}
        {/* Mobile: Full width with height = viewport width, Desktop: Flex-1 on left */}
        <div className={`${isMobileViewport ? 'w-full' : 'flex-1'} flex flex-col ${isMobileViewport ? '' : 'h-full'}`}>
          {/* Map Container */}
          <div 
            className="dashboard-card map-card relative w-full rounded-xl overflow-hidden shadow-xl flex-shrink-0"
            style={isMobileViewport ? {
              height: `${viewportWidth}px`, // Height equals viewport width on mobile
              width: '100%'
            } : {
              height: '100%',
              width: '100%'
            }}
          >
            {/* Map Component */}
            <MapComponent
              ref={mapComponentRef}
              officeLocations={officeLocations}
              guardLocations={employees.filter(emp => 
                emp.role === 'Guard' && 
                emp.lat != null && 
                emp.lng != null
              )}
              patrolQRLocations={patrolQRLocations} // Show QR path for selected patrol
              allPatrolRoutes={allPatrolRoutes} // Always show all patrol routes (built from real QR location data)
              selectedPatrolId={selectedPatrolForMap} // Pass selected patrol ID for highlighting
              patrolEmployees={patrolEmployees} // Pass real employees assigned to the selected patrol
              onMarkerClick={() => {}} // No site selection in Patrolling view
              mapCenter={mapCenter}
              mapZoom={mapZoom}
              activeRoutes={null} // Route is now handled by Directions Service
              showDrawingTools={false} // Disable drawing tools for Patrolling dashboard
            />
          </div>
        </div>

        {/* Live Patrols Sidebar */}
        {/* Mobile: Full width below map, Desktop: Fixed width on right */}
        <div 
          className={`${isMobileViewport ? 'w-full flex-1' : 'w-96 flex-shrink-0'} overflow-hidden bg-white/10 backdrop-blur-md border border-white/10 rounded-xl shadow-lg`}
          style={isMobileViewport ? {
            height: 'auto',
            minHeight: '300px'
          } : {
            height: '100%'
          }}
        >
          <TrackPatrollingComponent
            livePatrols={livePatrols}
            officeLocations={officeLocations}
            employees={employees}
            onDispatchNewPatrol={handleDispatchNewPatrol}
            onClose={() => {}} // No close functionality in dedicated view
            onSelectPatrolForMap={setSelectedPatrolForMap}
            selectedPatrolIdForMap={selectedPatrolForMap}
            onShowOfficerInfo={handleShowPatrolInfoTable}
            hideCloseButton={true} // Hide close button in dedicated dashboard view
            onRefresh={() => fetchLivePatrols(true)} // Pass refresh function with force refresh
            onOfficerClick={setSelectedOfficer} // Handler for officer click to show traveled path
            selectedOfficer={selectedOfficer} // Currently selected officer
            qrLookup={qrLookup} // Pass QR lookup map for resolving QR IDs to names
          />
        </div>
      </div>

      {/* Patrol Info Table Modal */}
      <AnimatePresence>
        {showPatrolInfoTable && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="w-full h-full bg-white rounded-xl shadow-lg p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4 flex-wrap">
                  <Typography variant="h5" className="text-black font-bold font-['Figtree']">
                    Patrol Information
                  </Typography>
                  
                  {/* Group Tabs */}
                  {getGroupsFromTableData.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setSelectedGroupIndex(null)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          selectedGroupIndex === null
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        All Groups
                      </button>
                      {getGroupsFromTableData.map((group, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedGroupIndex(index)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            selectedGroupIndex === index
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {group.groupIndex !== null 
                            ? `Group ${group.groupIndex + 1}` 
                            : (group.vehicleId !== 'N/A' ? `Vehicle ${group.vehicleId}` : `Group ${index + 1}`)}
                          {group.count > 0 && (
                            <span className="ml-2 text-xs opacity-75">({group.count})</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={handleClosePatrolInfoTable}
                  className="w-8 h-8 bg-neutral-50/50 rounded-full shadow-lg flex items-center justify-center"
                >
                  <XCircle className="w-6 h-6 text-black" />
                </button>
              </div>
              
              {/* Patrol Table Content */}
              <div className="w-full overflow-x-auto overflow-y-auto custom-scrollbar" style={{ maxHeight: 'calc(100% - 140px)' }}>
                {loadingTableData ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="w-8 h-8 border-2 border-gray-400 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading patrol information...</p>
                    </div>
                  </div>
                ) : (
                <table className="min-w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 bg-neutral-50/10 rounded-tl-lg text-center text-black text-lg font-semibold font-['Open_Sans'] leading-normal border-r border-neutral-300">S.No</th>
                      <th className="px-4 py-3 bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,_rgba(10,_128,_137,_0.09)_0%,_rgba(255,_255,_255,_0.72)_100%)] text-center text-black text-lg font-semibold font-['Open_Sans'] leading-normal border-r border-neutral-300">Employee</th>
                      <th className="px-4 py-3 bg-neutral-50/10 text-center text-black text-lg font-semibold font-['Open_Sans'] leading-normal border-r border-neutral-300">Driver</th>
                      <th className="px-4 py-3 bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,_rgba(10,_128,_137,_0.09)_0%,_rgba(255,_255,_255,_0.72)_100%)] text-center text-black text-lg font-semibold font-['Open_Sans'] leading-normal border-r border-neutral-300">Vehicle</th>
                      <th className="px-4 py-3 bg-neutral-50/10 text-center text-black text-lg font-semibold font-['Open_Sans'] leading-normal border-r border-neutral-300">Material</th>
                      <th className="px-4 py-3 bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,_rgba(10,_128,_137,_0.09)_0%,_rgba(255,_255,_255,_0.72)_100%)] rounded-tr-lg text-center text-black text-lg font-semibold font-['Open_Sans'] leading-normal">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(patrolTableData) && patrolTableData.length > 0 ? (
                      patrolTableData.map((row, index) => {
                        const isEven = index % 2 === 0;
                        const rowBgClass = isEven ? 'bg-white' : 'bg-blue-50';
                        
                        // Check if this is patrol officer data
                        const isPatrolOfficerData = row.serialNumber !== undefined || row.userId !== undefined;
                        
                        if (isPatrolOfficerData) {
                          // Display patrol officer data with enriched information
                          return (
                            <tr key={row.userId || index} className={rowBgClass}>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.serialNumber || index + 1}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.officerName || 'N/A'}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.driverName || 'N/A'}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.vehicleId || 'N/A'}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.material || 'N/A'}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed">{row.notes || 'N/A'}</td>
                            </tr>
                          );
                        } else {
                          // Display patrol data (original format)
                          return (
                            <tr key={row.id || index} className={rowBgClass}>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{index + 1}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.officer || row.officerName || 'N/A'}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.driverName || 'N/A'}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.vehicleId || 'N/A'}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.material || 'N/A'}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed">{row.notes || 'N/A'}</td>
                            </tr>
                          );
                        }
                      })
                    ) : (
                      <tr>
                        <td colSpan="6" className="py-4 text-center text-gray-600 bg-white/80">No patrol data available.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PatrollingDashboard;

